import { FC, ReactNode } from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { UserNotificationType } from '@shared/apis/notifications';
import { AlertTriangleIcon } from '@shared/icons/alert-triangle';
import { ChartBreakoutSquareIcon } from '@shared/icons/chart-breakout-square';
import { EyeOffIcon } from '@shared/icons/eye-off';
import { InboxIcon } from '@shared/icons/inbox';
import { MailIcon } from '@shared/icons/mail';
import { colours } from '@shared/theme/colours';

import { styles } from './NotificationTypeIcon.styles';

export interface NotificationTypeIconProps extends WithStyles<typeof styles> {
  muted?: boolean;
  type: UserNotificationType;
}

// Everything about to drop off the marketplace, single or grouped and whether it is a
// disposal or a requirement. Escalation beats object type here: the glyph's job is to say
// "this one has a deadline", and the row's own copy already says which object it is.
const EXPIRY_TYPES: UserNotificationType[] = [
  'disposals_marketplace_stale',
  'disposals_marketplace_stale_final',
  'requirements_marketplace_stale',
  'requirements_marketplace_stale_final',
];

const glyph = (type: UserNotificationType, colour: string): ReactNode => {
  switch (type) {
    case 'disposals_marketplace_stale':
    case 'disposals_marketplace_stale_final':
    case 'requirements_marketplace_stale':
    case 'requirements_marketplace_stale_final':
      return <AlertTriangleIcon color={colour} />;
    case 'disposals_marketplace_unpublished':
    case 'requirements_marketplace_unpublished':
      return <EyeOffIcon color={colour} />;
    case 'disposal_match_message_received':
    case 'requirement_match_message_received':
      return <MailIcon color={colour} />;
    case 'disposal_match_request_received':
    case 'requirement_match_request_received':
      return <InboxIcon color={colour} />;
    case 'schedule_invite_received':
      return <ChartBreakoutSquareIcon color={colour} />;
  }
};

const NotificationTypeIconComponent: FC<NotificationTypeIconProps> = ({ classes, muted, type }) => {
  const isExpiry = EXPIRY_TYPES.includes(type);

  // warning[600] rather than the [500] base: [500] is only 2.26:1 on the panel's white, so
  // the escalation would be paler than the neutral glyph it is meant to stand out from.
  // Seen/actioned rows mute to neutral[300] regardless of type — the escalation colour is
  // for rows still asking for attention.
  const colour = muted ? colours.neutral[300] : isExpiry ? colours.warning[600] : colours.neutral[500];

  return <span className={classes.root}>{glyph(type, colour)}</span>;
};

export const NotificationTypeIcon = withStyles(styles)(NotificationTypeIconComponent);
